import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createLead,
  getLeadDetails,
  getLeadList,
  updateAssignLead,
  updateLeadStatus,
} from '../services/leadService';
import {
  getStatusDrop,
  getLeadTypeDropValues,
  getLeadTypeProductValues,
  getLeadTypeBankValues,
  getLeadTypeBranchValues,
  getLeadTypeDropdownValues,
} from '../services/dropDownService';
import { LeadInitialStateType } from '../utils/Types';
import { API_STATUS, BlankPagination } from '../utils/Constant';
import { isAPISuccess } from '../utils/Util';

const initialState: LeadInitialStateType = {
  leadListData: {
    result: [],
    pagination: BlankPagination,
  },
  leadTypeOptions: [],
  statusDropOptions: [],
  leadTypeProductOptions: [],
  leadTypeBankOptions: [],
  leadTypeTenantStatus: [],
  leadTypeLoaneeStatus: [],
  leadTypeLobName: [],
  leadTypeBranchOptions: [],
  leadTypeRiskCoverage: [],
  leadTypeVehicleType: [],
  leadTypeOfCover: [],
  leadTypeCoverage: [],
  leadTypeCoverageFor: [],
  leadTypeInterestedIn: [],
  apiProgressStatus: '',
};

export const fetchLeadList = createAsyncThunk(
  'lead/fetchLeadList',
  async (data: any) => {
    const res = await getLeadList(data);
    return res.data;
  }
);

export const creteNewLead = createAsyncThunk(
  'lead/createLead',
  async (data: any) => {
    const res = await createLead(data);
    return res.data;
  }
);

export const assignLeadToOther = createAsyncThunk(
  'lead/assignLead',
  async (data: any) => {
    const res = await updateAssignLead(data);
    return res.data;
  }
);

export const fetchLeadDetails = createAsyncThunk(
  'lead/fetchLeadDetails',
  async (params: any) => {
    const res = await getLeadDetails(params);
    return res.data;
  }
);

export const updateLeadStatusDetails = createAsyncThunk(
  'lead/updateLeadStatusDetails',
  async (data: any) => {
    const res = await updateLeadStatus(data);
    return res.data;
  }
);

export const fetchLeadTypeDrops = createAsyncThunk(
  'lead/fetchLeadTypeDrops',
  async (params: any) => {
    const res = await getLeadTypeDropValues(params);
    return res.data;
  }
);

export const fetchLeadTypeProductDrops = createAsyncThunk(
  'lead/fetchLeadTypeProductDrops',
  async () => {
    const res = await getLeadTypeProductValues();
    return res.data;
  }
);

export const fetchLeadTypeBankDrops = createAsyncThunk(
  'lead/fetchLeadTypeBankDrops',
  async () => {
    const res = await getLeadTypeBankValues();
    return res.data;
  }
);

export const fetchLeadTypeDropdownDrops = createAsyncThunk(
  'lead/fetchLeadTypeDropdownDrops',
  async () => {
    const res = await getLeadTypeDropdownValues();
    return res.data;
  }
);

export const fetchLeadTypeBranchDrops = createAsyncThunk(
  'lead/fetchLeadTypeBranchDrops',
  async (params: any) => {
    const res = await getLeadTypeBranchValues(params);
    return res.data;
  }
);

export const fetchLeadStatusDrops = createAsyncThunk(
  'lead/fetchLeadStatusDrops',
  async (params: any) => {
    const res = await getStatusDrop(params);
    return res.data;
  }
);

export const leadSlice = createSlice({
  name: 'lead',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchLeadList.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        state.leadListData = action.payload;
      })
      .addCase(fetchLeadList.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(fetchLeadList.rejected, (state) => {
        state.apiProgressStatus = API_STATUS.FAILED;
      })
      .addCase(fetchLeadStatusDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.statusId,
            label: item.statusName,
          }));
          state.statusDropOptions = dropdowns;
        }
      })
      .addCase(fetchLeadTypeDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.leadTypeId,
            label: item.leadTypeName,
          }));
          state.leadTypeOptions = dropdowns;
        }
      })
      .addCase(fetchLeadTypeProductDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.productId,
            label: item.productName,
          }));
          state.leadTypeProductOptions = dropdowns;
        }
      })
      .addCase(fetchLeadTypeBankDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.bankId,
            label: item.bankName,
          }));
          state.leadTypeBankOptions = dropdowns;
        }
      })
      .addCase(fetchLeadTypeDropdownDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const tenantDropdowns = action.payload.result?.tenantStatus?.map(
            (item: any) => ({
              value: item.value,
              label: item.value,
            })
          );
          const LoaneeDropdowns = action.payload.result?.loaneeStatus?.map(
            (item: any) => ({
              value: item.value,
              label: item.value,
            })
          );
          const lobDropdown = action.payload.result?.lobName?.map(
            (item: any) => ({
              value: item.id,
              label: item.value,
            })
          );
          const riskCoverageDropdown = action.payload.result?.riskCoverage?.map(
            (item: any) => ({
              value: item.id,
              label: item.value,
            })
          );
          const vehicleTypeDropdown = action.payload.result?.vehicleType?.map(
            (item: any) => ({
              value: item.id,
              label: item.value,
            })
          );
          const typeOfCoverDropdown = action.payload.result?.typeOfCover?.map(
            (item: any) => ({
              value: item.id,
              label: item.value,
            })
          );
          const coverageDropdown = action.payload.result?.coverage?.map(
            (item: any) => ({
              value: item.id,
              label: item.value,
            })
          );
          const coverageForDropdown = action.payload.result?.coverageFor?.map(
            (item: any) => ({
              value: item.id,
              label: item.value,
            })
          );
          const interestedInDropdown = action.payload.result?.interestedIn?.map(
            (item: any) => ({
              value: item.id,
              label: item.value,
            })
          );
          state.leadTypeTenantStatus = tenantDropdowns;
          state.leadTypeLoaneeStatus = LoaneeDropdowns;
          state.leadTypeLobName = lobDropdown;
          state.leadTypeRiskCoverage = riskCoverageDropdown;
          state.leadTypeVehicleType = vehicleTypeDropdown;
          state.leadTypeOfCover = typeOfCoverDropdown;
          state.leadTypeCoverage = coverageDropdown;
          state.leadTypeCoverageFor = coverageForDropdown;
          state.leadTypeInterestedIn = interestedInDropdown;
        }
      })
      .addCase(fetchLeadTypeBranchDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.branchId,
            label: item.branchName,
          }));
          state.leadTypeBranchOptions = dropdowns;
        }
      });
  },
});

export const leadSelector = (state: { lead: LeadInitialStateType }) =>
  state.lead;

export const leadApiStatus = (state: { lead: LeadInitialStateType }) =>
  state.lead.apiProgressStatus;

export default leadSlice.reducer;
